import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import {
    type IModel,
    ITridentResource,
    LakehouseLinkedService,
    LinkedService,
    ReferenceAdapter,
    SqlAnalyticsEndpointLinkedService
} from 'shell-models-lib';
import { LinkedServiceAdapter } from './linkedservice.adapter';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SqlAnalyticsEndpointLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                endpoint: '*',
                artifactId: '*',
                workspaceId: '*',
                parentObjectId: '*'
            },
            parameters: '*'
        }
    }
})
export class SqlAnalyticsEndpointLinkedServiceAdapter extends LinkedServiceAdapter<SqlAnalyticsEndpointLinkedService> {
    protected supportFirstClassParameterExperience = true;

    public async postToModel(model: SqlAnalyticsEndpointLinkedService): Promise<void> {
        if (
            stringIsNullOrEmpty(SqlAnalyticsEndpointLinkedService.ENDPOINT.templateValue(model)) &&
            stringIsNullOrEmpty(model.endpoint)
        ) {
            model.endpoint = (model.parentEntity as LakehouseLinkedService)?.sqlAnalyticsEndpoint;
        }
    }

    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Pick<Model.SqlAnalyticsEndpointLinkedServiceTypeProperties, 'artifactId' | 'workspaceId' | 'parentObjectId'> => {
        return {
            artifactId: tridentResource.objectId,
            workspaceId: tridentResource.workspaceObjectId,
            parentObjectId: tridentResource.parentArtifactObjectId
        };
    };

    protected modelConstructor(): new () => SqlAnalyticsEndpointLinkedService {
        return SqlAnalyticsEndpointLinkedService;
    }

    protected model(): IModel {
        return SqlAnalyticsEndpointLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(
        linkedService: SqlAnalyticsEndpointLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.SqlAnalyticsEndpointLinkedService;
        properties.type = Metamodel.modelFor(SqlAnalyticsEndpointLinkedService).name;

        properties.typeProperties = {
            endpoint: this.valueToResource(linkedService, SqlAnalyticsEndpointLinkedService.ENDPOINT),
            artifactId: this.valueToResource(linkedService, SqlAnalyticsEndpointLinkedService.ARTIFACT_ID),
            workspaceId: this.valueToResource(linkedService, SqlAnalyticsEndpointLinkedService.WORKSPACE_ID),
        };

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: SqlAnalyticsEndpointLinkedService
    ): Promise<SqlAnalyticsEndpointLinkedService> {
        const properties = linkedServiceResource.properties as Model.SqlAnalyticsEndpointLinkedService;
        const typeProperties: Model.SqlAnalyticsEndpointLinkedServiceTypeProperties = properties.typeProperties;

        ValueAdapter.toModel(linkedService, SqlAnalyticsEndpointLinkedService.ENDPOINT, typeProperties.endpoint);
        ValueAdapter.toModel(linkedService, SqlAnalyticsEndpointLinkedService.ARTIFACT_ID, typeProperties.artifactId);
        ValueAdapter.toModel(linkedService, SqlAnalyticsEndpointLinkedService.WORKSPACE_ID, typeProperties.workspaceId);
        linkedService.parentEntity = ReferenceAdapter.toProxyModel<LinkedService>(
            Metamodel.modelFor(LinkedService),
            {
                type: 'LinkedServiceReference',
                referenceName: typeProperties.parentObjectId,
                workspaceObjectId: typeProperties.workspaceId
            },
            this.context
        );

        return linkedService;
    }
}
